import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import Navbar from '../components/Navbar';
import './AdminPanel.css';

const AdminPanel = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [markets, setMarkets] = useState([]);
  const [fees, setFees] = useState([]);
  const [activeTab, setActiveTab] = useState('markets');
  const [loading, setLoading] = useState(false);
  const [showNewMarketForm, setShowNewMarketForm] = useState(false);
  const [editingMarket, setEditingMarket] = useState(null);
  const [showEditMarketModal, setShowEditMarketModal] = useState(false);
  
  const [newMarket, setNewMarket] = useState({
    market: {
      name: '',
      phone: '',
      email: '',
      exchange_rate: 89500,
      clone_from_market_id: '',
      clone_items: true
    },
    user: {
      username: '',
      password: '',
      preferred_language: 'en'
    }
  });

  // Fetch market stats
  const fetchMarketStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/auth/admin/market_stats/');
      setMarkets(response.data);
    } catch (error) {
      console.error('Error fetching market stats:', error);
      toast.error(t('errorFetchingData'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch fees
  const fetchFees = async () => {
    setLoading(true);
    try {
      const response = await api.get('/auth/admin/market_fees/');
      setFees(response.data);
    } catch (error) {
      console.error('Error fetching fees:', error);
      toast.error(t('errorFetchingData'));
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin
  useEffect(() => {
    if (user) {
      fetchMarketStats();
      fetchFees();
    }
  }, [user]);

  // Handle new market form change
  const handleMarketFormChange = (e, path) => {
    const { name, value } = e.target;
    setNewMarket(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  // Open edit market modal
  const openEditMarketModal = (market) => {
    setEditingMarket({
      id: market.id,
      name: market.name,
      phone: market.phone,
      email: market.email,
      exchange_rate: market.exchange_rate,
      is_active: market.is_active
    });
    setShowEditMarketModal(true);
  };

  // Close edit market modal
  const closeEditMarketModal = () => {
    setEditingMarket(null);
    setShowEditMarketModal(false);
  };

  // Handle edit market form change
  const handleEditMarketChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingMarket(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Save edited market
  const handleSaveEditMarket = async (e) => {
    e.preventDefault();
    if (!editingMarket.name || !editingMarket.phone || !editingMarket.email) {
      toast.error(t('fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.patch(`/auth/admin/${editingMarket.id}/update_market/`, {
        name: editingMarket.name,
        phone: editingMarket.phone,
        email: editingMarket.email,
        exchange_rate: parseFloat(editingMarket.exchange_rate),
        is_active: editingMarket.is_active
      });
      toast.success(t('marketUpdatedSuccess') || 'Market updated successfully');
      closeEditMarketModal();
      fetchMarketStats();
    } catch (error) {
      console.error('Error updating market:', error);
      toast.error(error.response?.data?.error || t('errorUpdatingMarket') || 'Error updating market');
    } finally {
      setLoading(false);
    }
  };

  // Submit new market
  const handleCreateMarket = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newMarket.market.name || !newMarket.market.phone || !newMarket.market.email) {
      toast.error(t('fillAllFields'));
      return;
    }
    if (!newMarket.user.username || !newMarket.user.password) {
      toast.error(t('fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...newMarket,
        market: {
          ...newMarket.market,
          clone_from_market_id: newMarket.market.clone_from_market_id || null,
          clone_items: !!newMarket.market.clone_items,
        }
      };
      const response = await api.post('/auth/admin/create_market/', payload);
      toast.success(t('marketCreatedSuccessfully'));
      setNewMarket({
        market: {
          name: '',
          phone: '',
          email: '',
          exchange_rate: 89500,
          clone_from_market_id: '',
          clone_items: true
        },
        user: {
          username: '',
          password: '',
          preferred_language: 'en'
        }
      });
      setShowNewMarketForm(false);
      fetchMarketStats();
    } catch (error) {
      console.error('Error creating market:', error);
      const errorMsg = error.response?.data?.error || t('errorCreatingMarket');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Mark fee as paid
  const handleMarkFeePaid = async (feeId) => {
    setLoading(true);
    try {
      await api.post('/auth/admin/mark_fee_paid/', { fee_id: feeId });
      toast.success(t('feePaidSuccessfully'));
      fetchFees();
    } catch (error) {
      console.error('Error marking fee as paid:', error);
      toast.error(t('errorMarkingFeePaid'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="admin-panel-container" dir={i18n.dir()}>
        <div className="admin-header">
          <h1>{t('adminPanel') || 'Admin Panel'}</h1>
          <p>{t('manageMarkets') || 'Manage all markets and view statistics'}</p>
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'markets' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('markets');
              fetchMarketStats();
            }}
          >
            {t('markets') || 'Markets'} ({markets.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'fees' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('fees');
              fetchFees();
            }}
          >
            {t('fees') || 'Fees'} ({fees.length})
          </button>
        </div>

        {loading && <div className="loading">{t('loading')}...</div>}

        {/* Markets Tab */}
        {activeTab === 'markets' && !loading && (
          <div className="admin-content">
            <div className="add-market-btn-container">
              <button
                className="btn-primary add-market-btn"
                onClick={() => setShowNewMarketForm(!showNewMarketForm)}
              >
                {showNewMarketForm ? t('cancel') : t('addMarket') || 'Add New Market'}
              </button>
            </div>

            {/* New Market Form */}
            {showNewMarketForm && (
              <div className="new-market-form-container">
                <h2>{t('createNewMarket') || 'Create New Market'}</h2>
                <form onSubmit={handleCreateMarket} className="new-market-form">
                  <div className="form-section">
                    <h3>{t('marketInfo') || 'Market Information'}</h3>
                    <div className="form-group">
                      <label>{t('marketName')}:</label>
                      <input
                        type="text"
                        value={newMarket.market.name}
                        onChange={(e) => handleMarketFormChange(e, 'market.name')}
                        placeholder={t('enterMarketName')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('phone')}:</label>
                      <input
                        type="tel"
                        value={newMarket.market.phone}
                        onChange={(e) => handleMarketFormChange(e, 'market.phone')}
                        placeholder={t('enterPhone')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('email')}:</label>
                      <input
                        type="email"
                        value={newMarket.market.email}
                        onChange={(e) => handleMarketFormChange(e, 'market.email')}
                        placeholder={t('enterEmail')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('exchangeRate')}:</label>
                      <input
                        type="number"
                        value={newMarket.market.exchange_rate}
                        onChange={(e) => handleMarketFormChange(e, 'market.exchange_rate')}
                        step="0.01"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('cloneFromMarket') || 'Clone from market (optional)'}:</label>
                      <select
                        value={newMarket.market.clone_from_market_id}
                        onChange={(e) => handleMarketFormChange(e, 'market.clone_from_market_id')}
                      >
                        <option value="">{t('none') || 'None'}</option>
                        {markets.map((market) => (
                          <option key={market.id} value={market.id}>
                            {market.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={!!newMarket.market.clone_items}
                          onChange={(e) => handleMarketFormChange({ target: { name: 'clone_items', value: e.target.checked } }, 'market.clone_items')}
                          disabled={!newMarket.market.clone_from_market_id}
                        />{' '}
                        {t('cloneItemsToo') || 'Clone items too'}
                      </label>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>{t('ownerAccount') || 'Owner Account'}</h3>
                    <div className="form-group">
                      <label>{t('username')}:</label>
                      <input
                        type="text"
                        value={newMarket.user.username}
                        onChange={(e) => handleMarketFormChange(e, 'user.username')}
                        placeholder={t('enterUsername')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('password')}:</label>
                      <input
                        type="password"
                        value={newMarket.user.password}
                        onChange={(e) => handleMarketFormChange(e, 'user.password')}
                        placeholder={t('enterPassword')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('language')}:</label>
                      <select
                        value={newMarket.user.preferred_language}
                        onChange={(e) => handleMarketFormChange(e, 'user.preferred_language')}
                      >
                        <option value="en">English</option>
                        <option value="ar">العربية</option>
                        <option value="fr">Français</option>
                      </select>
                    </div>
                  </div>

                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? t('creating') : t('createMarket')}
                  </button>
                </form>
              </div>
            )}

            {/* Markets Table */}
            <div className="markets-table-container">
              <table className="markets-table">
                <thead>
                  <tr>
                    <th>{t('marketName')}</th>
                    <th>{t('items')}</th>
                    <th>{t('totalSalesUSD')}</th>
                    <th>{t('totalSalesLBP')}</th>
                    <th>{t('totalProfitUSD')}</th>
                    <th>{t('totalProfitLBP')}</th>
                    <th>{t('fesDue')}</th>
                    <th>{t('users')}</th>
                    <th>{t('established')}</th>
                    <th>{t('status')}</th>
                    <th>{t('storeLink')}</th>
                    <th>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {markets.map(market => (
                    <tr key={market.id}>
                      <td className="market-name">
                        <div>
                          <strong>{market.name}</strong>
                          <small>{market.phone}</small>
                        </div>
                      </td>
                      <td>{market.total_items}</td>
                      <td>${market.total_sales_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td>{market.total_sales_lbp.toLocaleString('en-US', { minimumFractionDigits: 0 })} LL</td>
                      <td>${market.total_profit_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td>{market.total_profit_lbp.toLocaleString('en-US', { minimumFractionDigits: 0 })} LL</td>
                      <td className="fees-due">${market.fees_due.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td>{market.user_count}</td>
                      <td>{new Date(market.created_at).toLocaleDateString(i18n.language)}</td>
                      <td>
                        <span className={`status-badge ${market.is_active ? 'active' : 'inactive'}`}>
                          {market.is_active ? t('active') : t('inactive')}
                        </span>
                      </td>
                      <td className="store-link-cell">
                        {market.store_link ? (
                          <div className="store-link-actions">
                            <a 
                              href={market.store_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="store-link-button"
                              title={t('openStore') || 'Open Store'}
                            >
                              🔗
                            </a>
                            <button
                              className="copy-link-button"
                              onClick={() => {
                                navigator.clipboard.writeText(market.store_link);
                                toast.success(t('linkCopied') || 'Link copied');
                              }}
                              title={t('copyLink') || 'Copy Link'}
                            >
                              📋
                            </button>
                          </div>
                        ) : (
                          <span className="no-owner">{t('noOwner') || 'No owner'}</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn-small btn-edit"
                          onClick={() => openEditMarketModal(market)}
                          title={t('edit') || 'Edit'}
                        >
                          ✏️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Fees Tab */}
        {activeTab === 'fees' && !loading && (
          <div className="admin-content">
            <div className="fees-table-container">
              <table className="fees-table">
                <thead>
                  <tr>
                    <th>{t('market')}</th>
                    <th>{t('amount')}</th>
                    <th>{t('reason')}</th>
                    <th>{t('dateCreated')}</th>
                    <th>{t('status')}</th>
                    <th>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {fees.length > 0 ? (
                    fees.map(fee => (
                      <tr key={fee.id} className={fee.paid ? 'paid' : 'unpaid'}>
                        <td>{fee.market}</td>
                        <td className="amount">${fee.amount_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td>{fee.reason}</td>
                        <td>{new Date(fee.created_at).toLocaleDateString(i18n.language)}</td>
                        <td>
                          <span className={`status-badge ${fee.paid ? 'paid' : 'pending'}`}>
                            {fee.paid ? `${t('paid')} - ${new Date(fee.paid_date).toLocaleDateString(i18n.language)}` : t('pending')}
                          </span>
                        </td>
                        <td>
                          {!fee.paid && (
                            <button
                              className="btn-small btn-success"
                              onClick={() => handleMarkFeePaid(fee.id)}
                              disabled={loading}
                            >
                              {t('markPaid')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-data">{t('noFeesFound')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Market Modal */}
      {showEditMarketModal && editingMarket && (
        <div className="modal-overlay" onClick={closeEditMarketModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('editMarket') || 'Edit Market'}</h2>
              <button className="modal-close" onClick={closeEditMarketModal}>&times;</button>
            </div>
            <form onSubmit={handleSaveEditMarket} className="edit-market-form">
              <div className="form-group">
                <label>{t('marketName')}:</label>
                <input
                  type="text"
                  name="name"
                  value={editingMarket.name}
                  onChange={handleEditMarketChange}
                  placeholder={t('enterMarketName')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('phone')}:</label>
                <input
                  type="tel"
                  name="phone"
                  value={editingMarket.phone}
                  onChange={handleEditMarketChange}
                  placeholder={t('enterPhone')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('email')}:</label>
                <input
                  type="email"
                  name="email"
                  value={editingMarket.email}
                  onChange={handleEditMarketChange}
                  placeholder={t('enterEmail')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('exchangeRate')}:</label>
                <input
                  type="number"
                  name="exchange_rate"
                  value={editingMarket.exchange_rate}
                  onChange={handleEditMarketChange}
                  step="0.01"
                />
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={editingMarket.is_active}
                    onChange={handleEditMarketChange}
                  />
                  {t('active')}
                </label>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeEditMarketModal}
                  disabled={loading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? t('saving') : t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
